import type { BrandingData } from '@scio3/shared-types';

/**
 * Canvas branding payload — subset of BrandingData sent to canvas.optisigns.com
 * via base64-encoded query param. Matches scio1 `clientUtils.js:buildCanvasBranding()`.
 */
interface CanvasBranding {
  baseUrl: string;
  branding: boolean;
  colorTheme: string;
  favicon: string;
  isWhiteLabel: boolean;
  logo: string;
  logoCompany: string;
  removeOptiSignsBranding: boolean;
  hideOptiSignsBrandedApps: boolean;
  siteTitle: string;
  templateCategories: string[];
  language: string;
}

/**
 * Build the branding object sent to the Canvas iframe.
 * Matches scio1 `clientUtils.js:buildCanvasBranding(domain)` (lines 506-522).
 */
export function buildCanvasBranding(brandingData: BrandingData, language: string): CanvasBranding {
  return {
    baseUrl: window.location.origin,
    branding: brandingData.branding,
    colorTheme: brandingData.colorTheme,
    favicon: brandingData.favicon,
    isWhiteLabel: brandingData.isWhiteLabel,
    logo: brandingData.logo,
    logoCompany: brandingData.logoCompany,
    removeOptiSignsBranding: brandingData.removeOptiSignsBranding,
    hideOptiSignsBrandedApps: brandingData.hideOptiSignsBrandedApps,
    siteTitle: brandingData.siteTitle,
    templateCategories: brandingData.templateCategories,
    language: language || 'en',
  };
}

/**
 * Encode branding as base64 UTF-8 string for the Canvas iframe URL.
 * Matches scio1 `clientUtils.js:getCanvasBrandingEncode(domain)` (lines 563-566).
 *
 * scio1 uses `Buffer.from(data, 'utf8').toString('base64')` — browser equivalent below.
 */
export function encodeCanvasBranding(brandingData: BrandingData, language: string): string {
  const branding = buildCanvasBranding(brandingData, language);
  const json = JSON.stringify(branding);
  // Browser equivalent of Node's Buffer.from(json, 'utf8').toString('base64')
  return btoa(unescape(encodeURIComponent(json)));
}
